import { FleetTenantStatus } from "api/fleet";

/** "42%"; null and NaN become "—" so missing data never reads as zero. */
export const formatPct = (value: number | null | undefined, digits = 0): string =>
  typeof value === "number" && Number.isFinite(value) ? `${value.toFixed(digits)}%` : "—";

/** 512 → "512 MB", 3072 → "3.0 GB". */
export const formatMemory = (mb: number | null | undefined): string => {
  if (typeof mb !== "number" || !Number.isFinite(mb)) {
    return "—";
  }
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${Math.round(mb)} MB`;
};

/** Seconds → "12 d 4 h", "3 h 20 m" or "45 m". */
export const formatDuration = (seconds: number | null | undefined): string => {
  if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds < 0) {
    return "—";
  }
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) {
    return `${days} d ${hours % 24} h`;
  }
  if (hours > 0) {
    return `${hours} h ${minutes % 60} m`;
  }
  return `${Math.max(minutes, 1)} m`;
};

const timeOfDay = new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

/** "14:05" in IST, for chart axes and timestamps. */
export const formatTimeOfDay = (value: string): string => timeOfDay.format(new Date(value));

const istDay = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" });

/** "14:05" for today (IST), "yesterday 18:30" for the day before, else a short date and time. */
export const formatWhen = (value: string, now: number = Date.now()): string => {
  const day = istDay.format(new Date(value));
  if (day === istDay.format(new Date(now))) {
    return formatTimeOfDay(value);
  }
  if (day === istDay.format(new Date(now - 24 * 60 * 60 * 1000))) {
    return `yesterday ${formatTimeOfDay(value)}`;
  }
  return `${day} ${formatTimeOfDay(value)}`;
};

export const TENANT_STATUS: Record<
  FleetTenantStatus,
  { label: string; tone: "success" | "warning" | "danger" | "neutral" }
> = {
  up: { label: "Up", tone: "success" },
  degraded: { label: "Degraded", tone: "warning" },
  down: { label: "Down", tone: "danger" },
  unknown: { label: "Unknown", tone: "neutral" },
};

/** How full a server resource is against the dashboard limits (default 75% warns, 90% is critical). */
export type UsageLevel = "normal" | "warning" | "danger";

export interface UsageLimits {
  warnPct: number;
  criticalPct: number;
}

export const DEFAULT_USAGE_LIMITS: UsageLimits = { warnPct: 75, criticalPct: 90 };

export const usageLevel = (
  pct: number | null | undefined,
  limits: UsageLimits = DEFAULT_USAGE_LIMITS
): UsageLevel => {
  if (typeof pct !== "number" || !Number.isFinite(pct)) {
    return "normal";
  }
  if (pct >= limits.criticalPct) {
    return "danger";
  }
  return pct >= limits.warnPct ? "warning" : "normal";
};
